import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import '@/App.css';

const Login = lazy(() => import('./pages/Login'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const RegisterCompany = lazy(() => import('./pages/RegisterCompany'));
const SuperAdminLogin = lazy(() => import('./pages/SuperAdminLogin'));
const SuperAdminDashboard = lazy(() => import('./pages/SuperAdminDashboard'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Employees = lazy(() => import('./pages/Employees'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const Attendance = lazy(() => import('./pages/Attendance'));
const Leaves = lazy(() => import('./pages/Leaves'));
const Holidays = lazy(() => import('./pages/Holidays'));
const PayrollProcessing = lazy(() => import('./pages/PayrollProcessing'));
const PayrollApprovals = lazy(() => import('./pages/PayrollApprovals'));
const StatutoryConfiguration = lazy(() => import('./pages/StatutoryConfiguration'));
const PayrollInputs = lazy(() => import('./pages/PayrollInputs'));
const Payslips = lazy(() => import('./pages/Payslips'));
const PaymentsAccounting = lazy(() => import('./pages/PaymentsAccounting'));
const FullFinalSettlements = lazy(() => import('./pages/FullFinalSettlements'));
const SalaryStructure = lazy(() => import('./pages/SalaryStructureV2'));
const SalaryComponents = lazy(() => import('./pages/SalaryComponents'));
const Payroll = lazy(() => import('./pages/Payroll'));
const Reports = lazy(() => import('./pages/Reports'));
const PayrollReporting = lazy(() => import('./pages/PayrollReporting'));
const CompanySettings = lazy(() => import('./pages/CompanySettings'));
const AdminSettingsHub = lazy(() => import('./pages/AdminSettingsHub'));
const Masters = lazy(() => import('./pages/Masters'));
const InfoHub = lazy(() => import('./pages/InfoHub'));
const MyDocuments = lazy(() => import('./pages/MyDocuments'));
const Feeds = lazy(() => import('./pages/Feeds'));
const Profile = lazy(() => import('./pages/Profile'));
const HelpCenter = lazy(() => import('./pages/HelpCenter'));
const SystemLogs = lazy(() => import('./pages/SystemLogs'));

const LEGACY_IVHR_HOSTS = new Set([
  'ivhrms.ivcargo.in',
  'ivhrm.ivcargo.in'
]);

const redirectLegacyIvhrHost = () => {
  if (typeof window === 'undefined') return false;
  if (!LEGACY_IVHR_HOSTS.has(window.location.hostname.toLowerCase())) return false;
  const destination = `https://ivhr.in${window.location.pathname}${window.location.search}${window.location.hash}`;
  window.location.replace(destination);
  return true;
};

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 text-slate-600">
    Loading...
  </div>
);

function App() {
  if (redirectLegacyIvhrHost()) {
    return null;
  }

  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/register-company" element={<RegisterCompany />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            
            {/* Super Admin Routes */}
            <Route path="/super-admin/login" element={<SuperAdminLogin />} />
            <Route path="/super-admin/dashboard" element={<SuperAdminDashboard />} />
            
            {/* Protected Company Routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<ProtectedRoute moduleId="dashboard"><Dashboard /></ProtectedRoute>} />
              <Route path="employees" element={<ProtectedRoute roles={['Admin', 'Manager', 'HR']} moduleId="employees"><Employees /></ProtectedRoute>} />
              <Route path="payroll-processing" element={<ProtectedRoute moduleId="payroll"><PayrollProcessing /></ProtectedRoute>} />
              <Route path="payroll-approvals" element={<ProtectedRoute moduleId="payroll"><PayrollApprovals /></ProtectedRoute>} />
              <Route path="statutory-configuration" element={<ProtectedRoute moduleId="payroll"><StatutoryConfiguration /></ProtectedRoute>} />
              <Route path="payroll-inputs" element={<ProtectedRoute moduleId="payroll"><PayrollInputs /></ProtectedRoute>} />
              <Route path="payslips" element={<ProtectedRoute moduleId="payslips"><Payslips /></ProtectedRoute>} />
              <Route path="payments-accounting" element={<ProtectedRoute moduleId="payroll"><PaymentsAccounting /></ProtectedRoute>} />
              <Route path="full-final-settlements" element={<ProtectedRoute moduleId="payroll"><FullFinalSettlements /></ProtectedRoute>} />
              <Route path="users" element={<ProtectedRoute roles={['Admin']} moduleId="users"><UserManagement /></ProtectedRoute>} />
              <Route path="masters" element={<ProtectedRoute roles={['Admin']} moduleId="masters"><Masters /></ProtectedRoute>} />
              <Route path="attendance" element={<ProtectedRoute moduleId="attendance"><Attendance /></ProtectedRoute>} />
              <Route path="leaves" element={<ProtectedRoute moduleId="leaves"><Leaves /></ProtectedRoute>} />
              <Route path="compoff" element={<Navigate to="/attendance?tab=compoff" replace />} />
              <Route path="holidays" element={<ProtectedRoute moduleId="holidays"><Holidays /></ProtectedRoute>} />
              <Route path="salary-components" element={<ProtectedRoute moduleId="salary-structure"><SalaryComponents /></ProtectedRoute>} />
              <Route path="salary-structure" element={<ProtectedRoute moduleId="salary-structure"><SalaryStructure /></ProtectedRoute>} />
              <Route path="payroll" element={<ProtectedRoute moduleId="payroll"><Payroll /></ProtectedRoute>} />
              <Route path="reports" element={<ProtectedRoute roles={['Admin', 'Manager']} moduleId="reports"><Reports /></ProtectedRoute>} />
              <Route path="payroll-reports" element={<ProtectedRoute moduleId="payroll-reports"><PayrollReporting /></ProtectedRoute>} />
              <Route path="admin/settings" element={<ProtectedRoute roles={['Admin']} moduleId="company-settings"><AdminSettingsHub /></ProtectedRoute>} />
              <Route path="company-settings" element={<ProtectedRoute roles={['Admin']} moduleId="company-settings"><AdminSettingsHub /></ProtectedRoute>} />
              <Route path="info-hub" element={<ProtectedRoute moduleId="info-hub"><InfoHub /></ProtectedRoute>} />
              <Route path="my-documents" element={<MyDocuments />} />
              <Route path="feeds" element={<ProtectedRoute moduleId="feeds"><Feeds /></ProtectedRoute>} />
              <Route path="user-manual" element={<HelpCenter />} />
              <Route path="help-center" element={<Navigate to="/user-manual" replace />} />
              <Route path="system-logs" element={<SystemLogs />} />
              <Route path="profile" element={<Profile />} />
            </Route>

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
